<template>
	<component
		:is="as"
		v-touch-target
		:class="['link-button', blockClassModifiers]"
		v-bind="defaults"
	>
		<span class="link-button__text">
			<slot>{{ label }}</slot>
		</span>
		<FaIcon
			v-if="withIcon && !badgeText"
			class="link-button__icon"
			:icon
		/>
		<small
			v-if="badgeText?.toString().trim()"
			class="link-button__badge"
			v-text="badgeText"
		/>
	</component>
</template>
<script lang="ts" setup>
import FaIcon, { type FontAwesomeIcon } from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import { computed } from 'vue';
import { vTouchTarget } from 'src/directives/TouchTarget/touchTarget';

export interface Props {
	/**
	 * The HTML element to render as. Defaults to 'button'.
	 */
	as?: 'a' | 'button';
	/**
	 * The text to display inside the button.
	 */
	label?: string;
	/**
	 * Whether to show the icon. Defaults to true.
	 */
	withIcon?: boolean;
	/**
	 * The theme of the button. Can be `primary` or `inherit`. Defaults to `primary`.
	 */
	theme?: 'primary' | 'inherit';
	/**
	 * Whether the button text should be bold and `1.6rem` `font-size`. Defaults to `false`.
	 */
	bold?: boolean;
	/**
	 * A number or string to display as a badge.
	 * This will replace the icon if provided.
	 */
	badgeText?: string | number;
	/**
	 * The `FontAwesome` icon to display. If provided, it will replace the default icon.
	 * Defaults to `['fa-solid', 'fa-chevron-right']`.
	 */
	icon?: FontAwesomeIcon;
}

const {
	as = 'button',
	withIcon = true,
	theme = 'primary',
	bold = false,
	icon = ['fa-solid', 'fa-chevron-right'],
	badgeText = '',
} = defineProps<Props>();

const defaults = computed(() => (as === 'button' ? { type: 'button' } : {}));
const blockClassModifiers = computed(() => ({
	[`link-button--${theme}`]: theme,
	['link-button--bold']: bold,
	['link-button--with-badge']: !!badgeText,
}));
</script>
<style lang="scss" scoped>
.link-button {
	text-align: right;
	color: $color-text-upselling;
	padding: 0;
	background: none;
	border: none;
	display: inline-flex;
	place-items: center start;
	gap: 0.8rem;
	outline: inherit;

	&--with-badge {
		gap: 0.4rem;
	}

	@include focus-visible-state(inherit, $color-text-upselling);

	& > * {
		pointer-events: none;
	}

	&__text {
		white-space: nowrap;
		font-size: $font-small-2;
		line-height: 2.6rem;
		text-decoration: underline;

		&:empty {
			display: none;
		}
	}

	&__icon {
		width: 1.2rem;
		height: 1.2rem;
		color: currentcolor;
		text-align: center;
	}

	&__badge {
		font-size: $font-small-3;
		background-color: $color-text-upselling;
		color: $color-white;
		padding: 0.4rem 0.8rem;
		border-radius: $border-radius-button;
		font-weight: $font-weight-bold;
		line-height: 1;
		vertical-align: middle;
	}

	&--bold {
		.link-button__text {
			font-size: $font-small-1;
			font-weight: $font-weight-semibold;
			line-height: 100%;
		}
	}

	&--inherit {
		color: inherit;
	}
}
</style>
